import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Stack,
  TableSortLabel
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Refresh,
  FilterList,
  Add,
  Download,
  UploadFile,
  Sync,
  CloudOff
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import { formatDateWithTimezone, getTimezoneLabel } from '../../utils/timezoneUtils';
import { getOfflineDraft, isNetworkError } from '../../utils/offlineTechlogStore';
import { useOfflineTechlogSync } from '../../hooks/useOfflineTechlogSync';
import TechnicalLogBookEntry from './TechnicalLogBookEntry';
import WorkflowGuide from '../common/WorkflowGuide';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const TechnicalLogBookList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [entries, setEntries] = useState([]); // fetched entries (server-side filters only)
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });
  const [filters, setFilters] = useState({
    aircraftId: '',
    status: 'ALL',
    dateFrom: null,
    dateTo: null,
    includeArchived: false,
    sheetNumberQuery: ''
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem('technicalLogBookPageSize');
    const parsed = raw ? Number(raw) : null;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'ENTRY_DATE',
    direction: 'desc'
  });
  const { user } = useAuth();
  const { showToast } = useToast();
  const csvImportInputRef = useRef(null);
  const fetchEntriesRef = useRef(null);

  const {
    isOnline,
    pendingCount,
    pendingEntryIds,
    syncing: offlineSyncing,
    flushPending,
    refreshPending
  } = useOfflineTechlogSync({
    onSynced: () => {
      if (typeof fetchEntriesRef.current === 'function') fetchEntriesRef.current();
    }
  });

  const handleOfflineDraftChange = useCallback(() => {
    refreshPending();
  }, [refreshPending]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';
  const tzLabel = getTimezoneLabel(displayTimezone);

  useEffect(() => {
    fetchAircraft();
    fetchEntries();
  }, []);

  useEffect(() => {
    // Only re-fetch when filters that affect the API URL change.
    fetchEntries();
    setPage(0);
  }, [filters.aircraftId, filters.includeArchived]);

  const openEntryId = location.state?.openEntryId;
  useEffect(() => {
    if (!openEntryId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.get(`/api/technical-log-book/${openEntryId}`);
        if (!cancelled) {
          setSelectedEntry(response.data);
          setEntryDialogOpen(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error opening entry:', err);
          if (isNetworkError(err) || (typeof navigator !== 'undefined' && !navigator.onLine)) {
            try {
              const draft = await getOfflineDraft(openEntryId);
              if (draft?.formSnapshot) {
                setSelectedEntry({
                  ...draft.formSnapshot,
                  _id: openEntryId,
                  _offlinePending: true,
                  _offlineUpdatedAt: draft.updatedAt
                });
                setEntryDialogOpen(true);
                showToast('Opened offline draft (no server connection)', 'info');
                navigate('/technical-log-book', { replace: true, state: {} });
                return;
              }
            } catch (draftErr) {
              console.error('Error loading offline draft:', draftErr);
            }
          }
          showToast('Failed to load entry', 'error');
        }
      }
      if (!cancelled) navigate('/technical-log-book', { replace: true, state: {} });
    };
    load();
    return () => { cancelled = true; };
  }, [openEntryId, navigate, showToast]);

  const fetchAircraft = async () => {
    try {
      const response = await axios.get('/api/aircraft');
      setAircraft(response.data);
    } catch (error) {
      console.error('Error fetching aircraft:', error);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      let url = '/api/technical-log-book';
      
      const params = new URLSearchParams();
      if (filters.aircraftId) {
        url = `/api/technical-log-book/aircraft/${filters.aircraftId}`;
      }
      if (filters.includeArchived) {
        params.set('includeArchived', 'true');
      }
      const qs = params.toString();
      if (qs) url = `${url}?${qs}`;
      
      const response = await axios.get(url);
      setEntries(response.data || []);
    } catch (error) {
      console.error('Error fetching Technical Log Book entries:', error);
      if (isNetworkError(error) || (typeof navigator !== 'undefined' && !navigator.onLine)) {
        showToast('Offline — showing cached list and any pending drafts', 'warning');
        await refreshPending();
      } else {
        showToast('Failed to load Technical Log Book entries', 'error');
      }
    } finally {
      setLoading(false);
    }
  };
  fetchEntriesRef.current = fetchEntries;

  const normalizedSheetNumberQuery = (filters.sheetNumberQuery || '').trim().toLowerCase();

  const clientFilteredEntries = useMemo(() => {
    let filtered = Array.isArray(entries) ? [...entries] : [];

    if (filters.status !== 'ALL') {
      filtered = filtered.filter(e => e.workflowStatus === filters.status);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(e => {
        const entryDate = dayjs(e.entryDate || e.createdAt);
        return entryDate.isAfter(filters.dateFrom) || entryDate.isSame(filters.dateFrom, 'day');
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter(e => {
        const entryDate = dayjs(e.entryDate || e.createdAt);
        return entryDate.isBefore(filters.dateTo) || entryDate.isSame(filters.dateTo, 'day');
      });
    }

    if (normalizedSheetNumberQuery) {
      filtered = filtered.filter(e => (e.sheetNumber || '').toLowerCase().includes(normalizedSheetNumberQuery));
    }

    return filtered;
  }, [entries, filters.dateFrom, filters.dateTo, filters.status, normalizedSheetNumberQuery]);

  const getSortValue = (entry, key) => {
    switch (key) {
      case 'SHEET_NUMBER':
        return entry.sheetNumber ? String(entry.sheetNumber).toLowerCase() : '';
      case 'AIRCRAFT':
        return entry.aircraftId?.registration ? String(entry.aircraftId.registration).toLowerCase() : '';
      case 'SECTOR': {
        const seq = entry.sectorId?.seqNo;
        // Sort "Standalone" deterministically (seqNo missing).
        return seq == null ? -1 : Number(seq);
      }
      case 'ENTRY_DATE': {
        const d = entry.entryDate || entry.createdAt;
        return d ? new Date(d).getTime() : 0;
      }
      case 'STATUS':
        return entry.workflowStatus ? String(entry.workflowStatus).toLowerCase() : '';
      case 'PHYSICAL_COPIES': {
        const score =
          (entry.whiteCopyReceived ? 1 : 0) +
          (entry.yellowCopyReceived ? 1 : 0) +
          (entry.pinkCopyReceived ? 1 : 0);
        return score;
      }
      case 'ACTIONS':
        return entry.createdAt ? new Date(entry.createdAt).getTime() : 0;
      default:
        return 0;
    }
  };

  const clientSortedEntries = useMemo(() => {
    const { key, direction } = sortConfig;
    const factor = direction === 'asc' ? 1 : -1;

    // Stable-ish sort: fallback to createdAt and _id.
    return [...clientFilteredEntries].sort((a, b) => {
      const av = getSortValue(a, key);
      const bv = getSortValue(b, key);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;

      const ad = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bd = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (ad < bd) return 1; // keep createdAt-desc feel when values tie
      if (ad > bd) return -1;
      return String(a._id || '').localeCompare(String(b._id || ''));
    });
  }, [clientFilteredEntries, sortConfig]);

  const paginatedEntries = useMemo(() => {
    const start = page * pageSize;
    return clientSortedEntries.slice(start, start + pageSize);
  }, [clientSortedEntries, page, pageSize]);

  useEffect(() => {
    setPage(0);
  }, [filters.status, filters.dateFrom, filters.dateTo, filters.sheetNumberQuery, sortConfig]);

  useEffect(() => {
    localStorage.setItem('technicalLogBookPageSize', String(pageSize));
  }, [pageSize]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
    setPage(0);
  };

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      filters.aircraftId ||
        filters.status !== 'ALL' ||
        filters.dateFrom ||
        filters.dateTo ||
        filters.includeArchived ||
        (filters.sheetNumberQuery || '').trim()
    );
  }, [filters]);

  const handleClearFilters = () => {
    setFilters({
      aircraftId: '',
      status: 'ALL',
      dateFrom: null,
      dateTo: null,
      includeArchived: false,
      sheetNumberQuery: ''
    });
    setSortConfig({ key: 'ENTRY_DATE', direction: 'desc' });
    setPage(0);
  };

  const formatCharterUnderSector = (trip) => {
    if (!trip || typeof trip !== 'object') return null;
    const number = trip.charterNumber?.trim();
    const title = trip.title?.trim();
    if (number && title) return `${number} — ${title}`;
    return number || title || null;
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'default',
      'SUBMITTED': 'warning',
      'UNDER_REVIEW': 'info',
      'APPROVED': 'success',
      'REJECTED': 'error'
    };
    return colors[status] || 'default';
  };

  const handleViewEntry = async (entryId) => {
    try {
      const response = await axios.get(`/api/technical-log-book/${entryId}`);
      setSelectedEntry(response.data);
      setEntryDialogOpen(true);
    } catch (error) {
      console.error('Error fetching entry:', error);
      if (isNetworkError(error) || (typeof navigator !== 'undefined' && !navigator.onLine)) {
        try {
          const draft = await getOfflineDraft(entryId);
          if (draft?.formSnapshot) {
            setSelectedEntry({
              ...draft.formSnapshot,
              _id: entryId,
              _offlinePending: true,
              _offlineUpdatedAt: draft.updatedAt
            });
            setEntryDialogOpen(true);
            showToast('Opened offline draft (no server connection)', 'info');
            return;
          }
        } catch (draftErr) {
          console.error('Error loading offline draft:', draftErr);
        }
      }
      showToast('Failed to load entry details', 'error');
    }
  };

  const handleCloseEntryDialog = () => {
    setEntryDialogOpen(false);
    setSelectedEntry(null);
    fetchEntries(); // Refresh list
  };

  const handleOpenEntryById = async (entryId) => {
    if (!entryId) return;
    await handleViewEntry(entryId);
  };

  const handleDownloadSectorCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.aircraftId) params.set('aircraftId', filters.aircraftId);
      if (filters.status && filters.status !== 'ALL') params.set('status', filters.status);
      if (filters.dateFrom) params.set('dateFrom', dayjs(filters.dateFrom).format('YYYY-MM-DD'));
      if (filters.dateTo) params.set('dateTo', dayjs(filters.dateTo).format('YYYY-MM-DD'));
      if (filters.includeArchived) params.set('includeArchived', 'true');
      if ((filters.sheetNumberQuery || '').trim()) params.set('sheetNumberQuery', filters.sheetNumberQuery.trim());

      const qs = params.toString();
      const url = `/api/technical-log-book/export/sectors.csv${qs ? `?${qs}` : ''}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const dateStamp = dayjs().format('YYYY-MM-DD');
      link.setAttribute('download', `technical-log-book-sectors-${dateStamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      showToast('Sector CSV exported successfully', 'success');
    } catch (error) {
      console.error('Error downloading sector CSV:', error);
      showToast('Failed to export sector CSV', 'error');
    }
  };

  const handleOpenCsvImportPicker = () => {
    if (csvImportInputRef.current) {
      csvImportInputRef.current.value = '';
      csvImportInputRef.current.click();
    }
  };

  const handleSectorCsvImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/api/technical-log-book/import/sectors.csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data || null);
      showToast('Sector CSV imported successfully', 'success');
      fetchEntries();
    } catch (error) {
      console.error('Error importing sector CSV:', error);
      const message = error.response?.data?.message || 'Failed to import sector CSV';
      showToast(message, 'error');
      setImportResult(error.response?.data || null);
    } finally {
      if (event.target) event.target.value = '';
    }
  };

  const handleApprove = async (entryId, remarks) => {
    try {
      await axios.post(`/api/technical-log-book/${entryId}/approve`, { remarks });
      showToast('Entry approved successfully', 'success');
      fetchEntries();
      handleCloseEntryDialog();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to approve entry', 'error');
    }
  };

  const handleReject = async (entryId, remarks) => {
    try {
      await axios.post(`/api/technical-log-book/${entryId}/reject`, { remarks });
      showToast('Entry rejected successfully', 'success');
      fetchEntries();
      handleCloseEntryDialog();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to reject entry', 'error');
    }
  };

  const handleStartReview = async (entryId, remarks) => {
    try {
      await axios.post(`/api/technical-log-book/${entryId}/start-review`, { remarks });
      showToast('Review started successfully', 'success');
      fetchEntries();
      handleCloseEntryDialog();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to start review', 'error');
    }
  };

  const canApprove = () => {
    return [ROLES.CA_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role);
  };

  const canEdit = (entry) => {
    if (entry.workflowStatus === 'APPROVED') return false;
    if (entry.workflowStatus === 'DRAFT') {
      return [
        ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER,
        ROLES.CA_MANAGER,
        ROLES.SUPER_ADMIN,
        ROLES.MRO_ENGINEER,
        ROLES.CREW,
        ROLES.OPERATIONS_MANAGER,
        ROLES.OPERATIONS_EXECUTIVE,
        ROLES.ACCOUNTABLE_MANAGER
      ].includes(user?.role);
    }
    return [ROLES.CA_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const canBackfill = [ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.CA_MANAGER].includes(user?.role);
  const canManageSectorCsv = [ROLES.MIS, ROLES.SUPER_ADMIN].includes(user?.role);
  const isPilot = user?.role === ROLES.CREW;

  return (
    <Box>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4">
            {isPilot ? 'My Technical Log Books' : 'Technical Log Book'}
          </Typography>
          {isPilot && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Only techlogs for your assigned charters. Open from Crew Inbox with Enter Techlog after the flight.
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!isOnline && (
            <Chip icon={<CloudOff />} label="Offline" color="warning" size="small" variant="outlined" />
          )}
          <input
            ref={csvImportInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleSectorCsvImport}
          />
          {canBackfill && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/technical-log-book/backfill')}
            >
              Create entry
            </Button>
          )}
          {canManageSectorCsv && (
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadSectorCsv}
            >
              Download Sector CSV
            </Button>
          )}
          {canManageSectorCsv && (
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              onClick={handleOpenCsvImportPicker}
            >
              Upload Sector CSV
            </Button>
          )}
          {pendingCount > 0 && isOnline && (
            <Button
              variant="outlined"
              color="info"
              startIcon={offlineSyncing ? <CircularProgress size={16} /> : <Sync />}
              onClick={async () => {
                const result = await flushPending();
                if (result.synced > 0) {
                  showToast(
                    `Synced ${result.synced} offline techlog${result.synced === 1 ? '' : 's'}`,
                    'success'
                  );
                  fetchEntries();
                } else if (result.failed > 0) {
                  showToast('Some offline techlogs failed to sync', 'error');
                } else {
                  showToast('No pending offline changes', 'info');
                }
              }}
              disabled={offlineSyncing}
            >
              Sync ({pendingCount})
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchEntries}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <WorkflowGuide variant="technicalLogBook" />

      {pendingCount > 0 && (
        <Alert
          severity={isOnline ? 'info' : 'warning'}
          sx={{ mb: 2 }}
          action={
            isOnline ? (
              <Button
                color="inherit"
                size="small"
                startIcon={offlineSyncing ? <CircularProgress size={14} color="inherit" /> : <Sync />}
                disabled={offlineSyncing}
                onClick={async () => {
                  const result = await flushPending();
                  if (result.synced > 0) {
                    showToast(
                      `Synced ${result.synced} offline techlog${result.synced === 1 ? '' : 's'}`,
                      'success'
                    );
                    fetchEntries();
                  } else if (result.failed > 0) {
                    showToast('Some offline techlogs failed to sync', 'error');
                  }
                }}
              >
                Sync now
              </Button>
            ) : null
          }
        >
          {pendingCount === 1
            ? '1 techlog has unsynced offline changes.'
            : `${pendingCount} techlogs have unsynced offline changes.`}
          {!isOnline ? ' They will sync automatically when you reconnect.' : ''}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            <FilterList sx={{ verticalAlign: 'middle', mr: 1 }} />
            Filters
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
          >
            Clear
          </Button>
        </Box>
        <Grid container spacing={2}>
          {!isPilot && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Aircraft</InputLabel>
              <Select
                value={filters.aircraftId}
                label="Aircraft"
                onChange={(e) => setFilters({ ...filters, aircraftId: e.target.value })}
              >
                <MenuItem value="">All Aircraft</MenuItem>
                {aircraft.map((ac) => (
                  <MenuItem key={ac._id} value={ac._id}>
                    {ac.registration} - {ac.model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          )}
          <Grid item xs={12} md={isPilot ? 4 : 3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="SUBMITTED">Submitted</MenuItem>
                <MenuItem value="UNDER_REVIEW">Under Review</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={isPilot ? 4 : 3}>
            <TextField
              fullWidth
              label="Sheet Number"
              value={filters.sheetNumberQuery}
              onChange={(e) => setFilters({ ...filters, sheetNumberQuery: e.target.value })}
              placeholder="Search by sheet number"
              helperText={!isPilot && !filters.aircraftId ? 'Select an aircraft to narrow results' : ' '}
            />
          </Grid>
          {!isPilot && (
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Include Archived</InputLabel>
              <Select
                value={filters.includeArchived ? 'YES' : 'NO'}
                label="Include Archived"
                onChange={(e) => setFilters({ ...filters, includeArchived: e.target.value === 'YES' })}
              >
                <MenuItem value="NO">No</MenuItem>
                <MenuItem value="YES">Yes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          )}
          <Grid item xs={12} md={isPilot ? 4 : 2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={tzLabel ? `Date From (${tzLabel})` : 'Date From'}
                value={filters.dateFrom}
                onChange={(date) => setFilters({ ...filters, dateFrom: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={isPilot ? 4 : 2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={tzLabel ? `Date To (${tzLabel})` : 'Date To'}
                value={filters.dateTo}
                onChange={(date) => setFilters({ ...filters, dateTo: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortConfig.key === 'SHEET_NUMBER' ? sortConfig.direction : undefined}>
                <TableSortLabel
                  active={sortConfig.key === 'SHEET_NUMBER'}
                  direction={sortConfig.key === 'SHEET_NUMBER' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('SHEET_NUMBER')}
                >
                  Sheet Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortConfig.key === 'AIRCRAFT' ? sortConfig.direction : undefined}>
                <TableSortLabel
                  active={sortConfig.key === 'AIRCRAFT'}
                  direction={sortConfig.key === 'AIRCRAFT' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('AIRCRAFT')}
                >
                  Aircraft
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortConfig.key === 'SECTOR' ? sortConfig.direction : undefined}>
                <TableSortLabel
                  active={sortConfig.key === 'SECTOR'}
                  direction={sortConfig.key === 'SECTOR' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('SECTOR')}
                >
                  Sector
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortConfig.key === 'ENTRY_DATE' ? sortConfig.direction : undefined}>
                <TableSortLabel
                  active={sortConfig.key === 'ENTRY_DATE'}
                  direction={sortConfig.key === 'ENTRY_DATE' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('ENTRY_DATE')}
                >
                  Entry Date
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortConfig.key === 'STATUS' ? sortConfig.direction : undefined}>
                <TableSortLabel
                  active={sortConfig.key === 'STATUS'}
                  direction={sortConfig.key === 'STATUS' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('STATUS')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortConfig.key === 'PHYSICAL_COPIES' ? sortConfig.direction : undefined}>
                <TableSortLabel
                  active={sortConfig.key === 'PHYSICAL_COPIES'}
                  direction={sortConfig.key === 'PHYSICAL_COPIES' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('PHYSICAL_COPIES')}
                >
                  Physical Copies
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortConfig.key === 'ACTIONS' ? sortConfig.direction : undefined}>
                <TableSortLabel
                  active={sortConfig.key === 'ACTIONS'}
                  direction={sortConfig.key === 'ACTIONS' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('ACTIONS')}
                >
                  Actions
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientSortedEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {isPilot
                      ? 'No techlogs for your assignments yet. After ATC sign-off, use Enter Techlog on Crew Inbox.'
                      : 'No Technical Log Book entries found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedEntries.map((entry) => {
                const charterLabel = entry.sectorId
                  ? formatCharterUnderSector(entry.sectorId.tripId)
                  : formatCharterUnderSector(entry.openedForTripId);
                const isCharterLinked = Boolean(
                  entry.openedForTripId || entry.sectorId?.tripId
                );
                return (
                <TableRow key={entry._id}>
                  <TableCell>
                    {entry.sheetNumber || <Typography variant="body2" color="text.secondary">Not set</Typography>}
                  </TableCell>
                  <TableCell>
                    {entry.aircraftId?.registration || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {entry.sectorId ? (
                      <Box>
                        <Typography variant="body2">
                          {entry.sectorId.seqNo != null ? `Sector ${entry.sectorId.seqNo}` : 'N/A'}
                        </Typography>
                        {charterLabel && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {charterLabel}
                          </Typography>
                        )}
                      </Box>
                    ) : isCharterLinked ? (
                      <Box>
                        <Typography variant="body2">Charter</Typography>
                        {charterLabel && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {charterLabel}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Chip label="Standalone" size="small" variant="outlined" color="info" />
                    )}
                  </TableCell>
                  <TableCell>
                    {displayTimezone
                      ? formatDateWithTimezone(entry.entryDate || entry.createdAt, displayTimezone)
                      : (entry.entryDate ? dayjs(entry.entryDate).format('DD MMM YYYY') : dayjs(entry.createdAt).format('DD MMM YYYY'))}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        label={entry.workflowStatus}
                        color={getStatusColor(entry.workflowStatus)}
                        size="small"
                      />
                      {pendingEntryIds.has(String(entry._id)) && (
                        <Chip label="Pending sync" size="small" color="info" variant="outlined" />
                      )}
                      {entry.isActive === false && (
                        <Chip label="ARCHIVED" size="small" variant="outlined" color="warning" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Chip label="W" size="small" color={entry.whiteCopyReceived ? 'success' : 'default'} />
                      <Chip label="Y" size="small" color={entry.yellowCopyReceived ? 'success' : 'default'} />
                      <Chip label="P" size="small" color={entry.pinkCopyReceived ? 'success' : 'default'} />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewEntry(entry._id)}
                      color="primary"
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={clientSortedEntries.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            const nextSize = parseInt(e.target.value, 10);
            setPageSize(nextSize);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {importResult && (
        <Alert
          severity={importResult.failedRows > 0 ? 'warning' : 'success'}
          sx={{ mt: 2 }}
          onClose={() => setImportResult(null)}
        >
          {`Import complete. Total rows: ${importResult.totalRows || 0}, Updated: ${importResult.updatedRows || 0}, Skipped: ${importResult.skippedRows || 0}, Failed: ${importResult.failedRows || 0}`}
          {Array.isArray(importResult.errors) && importResult.errors.length > 0 && (
            <Box component="ul" sx={{ mb: 0, mt: 1, pl: 2 }}>
              {importResult.errors.slice(0, 5).map((err, idx) => (
                <li key={`${err.rowNumber || idx}-${idx}`}>
                  {`Row ${err.rowNumber || '?'}: ${err.message || 'Unknown error'}`}
                </li>
              ))}
              {importResult.errors.length > 5 && (
                <li>{`...and ${importResult.errors.length - 5} more errors`}</li>
              )}
            </Box>
          )}
        </Alert>
      )}

      <TechnicalLogBookEntry
        open={entryDialogOpen}
        onClose={handleCloseEntryDialog}
        entry={selectedEntry}
        onOpenEntry={handleOpenEntryById}
        onApprove={handleApprove}
        onReject={handleReject}
        onStartReview={handleStartReview}
        onOfflineDraftChange={handleOfflineDraftChange}
        canApprove={canApprove()}
        canEdit={selectedEntry ? canEdit(selectedEntry) : false}
        displayTimezone={displayTimezone}
      />
    </Box>
  );
};

export default TechnicalLogBookList;
